/**
 * Site Detail Page - Container Control Panel
 * ==========================================
 * View all details about a specific site/container.
 * Control it, view logs, and access files!
 */

import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { 
  Container, 
  Play,
  Square,
  RefreshCw,
  Trash2,
  Cpu,
  MemoryStick,
  HardDrive,
  ExternalLink,
  Code2,
  ChevronLeft,
  Loader2,
  Terminal,
  Activity,
  Brain,
  LogOut,
  Flame,
  Clock,
  Globe,
  Server,
  FileCode,
  Trophy
} from "lucide-react";
import { toast } from "sonner";

// Sidebar Component
const Sidebar = ({ activePage }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const menuItems = [
    { id: "dashboard", label: "MISSION CONTROL", icon: <Activity className="w-5 h-5" />, path: "/dashboard" },
    { id: "sites", label: "DEPLOYMENTS", icon: <Container className="w-5 h-5" />, path: "/sites" },
    { id: "ml-studio", label: "ROBOT GYM", icon: <Brain className="w-5 h-5" />, path: "/ml-studio" },
    { id: "tutorials", label: "GUIDES", icon: <Trophy className="w-5 h-5" />, path: "/tutorials" },
    { id: "family", label: "FAMILY", icon: <Flame className="w-5 h-5" />, path: "/family" },
  ];

  return (
    <div className="w-64 bg-[#16191F] border-r border-white/5 flex flex-col min-h-screen">
      <div className="p-6 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
            <Terminal className="w-6 h-6 text-black" />
          </div>
          <span className="text-xl font-black text-white tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            CODEFORGE
          </span>
        </div>
      </div>

      <div className="p-4 m-4 bg-black/30 border border-primary/20">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-mono text-muted-foreground uppercase">LEVEL {user?.level || 1}</span>
          <span className="text-xs font-mono text-primary">{user?.xp || 0} XP</span>
        </div>
        <Progress value={(user?.xp || 0) % 100} className="h-2 bg-muted" />
        <div className="flex items-center gap-2 mt-3">
          <Flame className="w-4 h-4 text-orange-400" />
          <span className="text-xs font-mono text-white">{user?.streak || 0} day streak</span>
        </div>
      </div>

      <nav className="flex-1 p-4">
        <div className="space-y-1">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-mono transition-colors ${
                activePage === item.id
                  ? "bg-primary/10 text-primary border-l-2 border-primary"
                  : "text-muted-foreground hover:text-white hover:bg-white/5"
              }`}
              data-testid={`nav-${item.id}`}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      </nav>

      <div className="p-4 border-t border-white/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
              <span className="text-primary font-bold text-sm">{user?.username?.[0]?.toUpperCase()}</span>
            </div>
            <span className="text-sm font-mono text-white">{user?.username}</span>
          </div>
          <button onClick={logout} className="text-muted-foreground hover:text-red-400 transition-colors" data-testid="logout-btn">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

const SiteDetailPage = () => {
  const navigate = useNavigate();
  const { siteId } = useParams();
  const { refreshUser } = useAuth();
  const [site, setSite] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchSiteData();
  }, [siteId]);

  const fetchSiteData = async () => {
    try {
      const [siteRes, logsRes] = await Promise.all([
        api.get(`/sites/${siteId}`),
        api.get(`/sites/${siteId}/logs`),
      ]);
      setSite(siteRes.data);
      setLogs(logsRes.data.logs);
    } catch (error) {
      toast.error("Failed to load site");
      navigate("/sites");
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action) => {
    setActionLoading(true);
    try {
      await api.post(`/sites/${siteId}/action?action=${action}`);
      await fetchSiteData();
      if (action === "start" || action === "restart") {
        toast.success(`Site ${action}ed!`, { description: "+25 XP earned!" });
        await refreshUser();
      } else {
        toast.success(`Site ${action}ped`);
      }
    } catch (error) {
      toast.error(`Failed to ${action} site`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${site.name}"? This cannot be undone!`)) {
      return;
    }
    try {
      await api.delete(`/sites/${siteId}`);
      toast.success("Site deleted");
      navigate("/sites");
    } catch (error) {
      toast.error("Failed to delete site");
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-[#0B0D10]">
        <Sidebar activePage="sites" />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-2" />
            <p className="text-muted-foreground font-mono">Loading container data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!site) {
    return null;
  }

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="flex min-h-screen bg-[#0B0D10]">
      <Sidebar activePage="sites" />
      
      <div className="flex-1 overflow-auto">
        {/* Header */}
        <header className="border-b border-white/5 bg-[#16191F]/50 backdrop-blur-sm sticky top-0 z-10">
          <div className="px-8 py-4">
            <button
              onClick={() => navigate("/sites")}
              className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors font-mono text-sm mb-4"
              data-testid="back-to-sites-btn"
            >
              <ChevronLeft className="w-4 h-4" />
              BACK TO DEPLOYMENTS
            </button>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-4 h-4 rounded-full ${site.status === 'running' ? 'bg-green-400 status-online' : 'bg-red-400'}`} />
                <div>
                  <h1 
                    className="text-2xl font-bold text-white uppercase tracking-tight"
                    style={{ fontFamily: 'Chivo, sans-serif' }}
                    data-testid="site-detail-title"
                  >
                    {site.name}
                  </h1>
                  <p className="text-xs font-mono text-muted-foreground mt-1">
                    {site.container_id}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {site.status === 'running' ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => handleAction('stop')}
                      disabled={actionLoading}
                      className="border-red-400/30 text-red-400 hover:bg-red-400/10 font-mono"
                      data-testid="stop-btn"
                    >
                      <Square className="w-4 h-4 mr-2" /> STOP
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleAction('restart')}
                      disabled={actionLoading}
                      className="border-blue-400/30 text-blue-400 hover:bg-blue-400/10 font-mono"
                      data-testid="restart-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" /> RESTART
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={() => handleAction('start')}
                    disabled={actionLoading}
                    className="bg-green-500 text-black hover:bg-green-400 font-mono"
                    data-testid="start-btn"
                  >
                    <Play className="w-4 h-4 mr-2" /> START
                  </Button>
                )}
                <Button
                  onClick={() => navigate(`/sites/${siteId}/files`)}
                  className="bg-primary text-black font-bold hover:bg-primary/90"
                  data-testid="open-files-btn"
                >
                  <Code2 className="w-4 h-4 mr-2" /> CODE EDITOR
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="p-8">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-[#16191F] border border-white/5 p-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Cpu className="w-4 h-4" />
                <span className="text-xs font-mono uppercase">CPU USAGE</span>
              </div>
              <div className="text-3xl font-black text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {site.cpu_usage}%
              </div>
              <Progress value={site.cpu_usage} className="h-2 mt-2 bg-muted" />
            </div>

            <div className="bg-[#16191F] border border-white/5 p-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <MemoryStick className="w-4 h-4" />
                <span className="text-xs font-mono uppercase">MEMORY</span>
              </div>
              <div className="text-3xl font-black text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {site.memory_usage}MB
              </div>
              <Progress value={(site.memory_usage / 512) * 100} className="h-2 mt-2 bg-muted" />
            </div>

            <div className="bg-[#16191F] border border-white/5 p-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <HardDrive className="w-4 h-4" />
                <span className="text-xs font-mono uppercase">DISK</span>
              </div>
              <div className="text-3xl font-black text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {site.disk_usage}MB
              </div>
              <Progress value={(site.disk_usage / 1000) * 100} className="h-2 mt-2 bg-muted" />
            </div>

            <div className="bg-[#16191F] border border-white/5 p-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Server className="w-4 h-4" />
                <span className="text-xs font-mono uppercase">STATUS</span>
              </div>
              <div className={`text-3xl font-black uppercase ${site.status === 'running' ? 'text-green-400' : 'text-red-400'}`} style={{ fontFamily: 'Chivo, sans-serif' }}>
                {site.status}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="info" className="w-full">
            <TabsList className="bg-[#16191F] border border-white/5 p-1">
              <TabsTrigger 
                value="info" 
                className="font-mono text-sm data-[state=active]:bg-primary data-[state=active]:text-black"
              >
                INFO
              </TabsTrigger>
              <TabsTrigger 
                value="logs" 
                className="font-mono text-sm data-[state=active]:bg-primary data-[state=active]:text-black"
              >
                LOGS
              </TabsTrigger>
              <TabsTrigger 
                value="danger" 
                className="font-mono text-sm data-[state=active]:bg-red-500 data-[state=active]:text-white"
              >
                DANGER ZONE
              </TabsTrigger>
            </TabsList>

            {/* Info Tab */}
            <TabsContent value="info" className="mt-6">
              <div className="bg-[#16191F] border border-white/5 p-6">
                <h3 className="text-lg font-bold text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  CONTAINER DETAILS
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-mono text-muted-foreground uppercase">SITE NAME</label>
                      <p className="text-white font-mono mt-1">{site.name}</p>
                    </div>
                    <div>
                      <label className="text-xs font-mono text-muted-foreground uppercase">DOMAIN</label>
                      <div className="flex items-center gap-2 mt-1">
                        <Globe className="w-4 h-4 text-blue-400" />
                        <a 
                          href={`https://${site.domain}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-400 font-mono hover:underline"
                        >
                          {site.domain}
                        </a>
                        <ExternalLink className="w-3 h-3 text-muted-foreground" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-mono text-muted-foreground uppercase">CONTAINER ID</label>
                      <p className="text-white font-mono mt-1">{site.container_id}</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-mono text-muted-foreground uppercase">PHP VERSION</label>
                      <p className="text-white font-mono mt-1">PHP {site.php_version}</p>
                    </div>
                    <div>
                      <label className="text-xs font-mono text-muted-foreground uppercase">CREATED</label>
                      <div className="flex items-center gap-2 mt-1">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <p className="text-white font-mono">{formatDate(site.created_at)}</p>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-mono text-muted-foreground uppercase">DESCRIPTION</label>
                      <p className="text-white font-mono mt-1">{site.description || "No description"}</p>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Logs Tab */}
            <TabsContent value="logs" className="mt-6">
              <div className="bg-[#0D1117] border border-white/5 p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    CONTAINER LOGS
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchSiteData}
                    className="border-white/10 font-mono text-xs"
                    data-testid="refresh-logs-btn"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" /> REFRESH
                  </Button>
                </div>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-1 font-mono text-sm">
                    {logs.map((log, index) => (
                      <div 
                        key={index} 
                        className="flex items-start gap-3 py-1 hover:bg-white/5 px-2 -mx-2"
                      >
                        <span className="text-muted-foreground text-xs whitespace-nowrap">
                          {new Date(log.timestamp).toLocaleTimeString()}
                        </span>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          log.level === 'ERROR' ? 'bg-red-500/20 text-red-400' :
                          log.level === 'WARN' ? 'bg-yellow-500/20 text-yellow-400' :
                          log.level === 'DEBUG' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-green-500/20 text-green-400'
                        }`}>
                          {log.level}
                        </span>
                        <span className="text-white">{log.message}</span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </TabsContent>

            {/* Danger Zone Tab */}
            <TabsContent value="danger" className="mt-6">
              <div className="bg-red-500/5 border border-red-500/20 p-6">
                <h3 className="text-lg font-bold text-red-400 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  DANGER ZONE
                </h3>
                <p className="text-muted-foreground font-mono text-sm mb-6">
                  Actions here are permanent and cannot be undone. Be careful!
                </p>
                <Button
                  onClick={handleDelete}
                  variant="destructive"
                  className="bg-red-500 text-white hover:bg-red-600 font-mono"
                  data-testid="delete-site-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  DELETE THIS SITE
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
};

export default SiteDetailPage;
